import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { publicApi } from "@/lib/api";
import { useSiteTheme } from "@/components/public/useSiteTheme";
import SeoHead from "@/components/public/SeoHead";

const PAGES = {
  privacy: {
    title: "Privacy Policy",
    body: (site) => `
Last updated: ${new Date().toLocaleDateString()}.

This Privacy Policy describes how ${site.name} ("we", "us", "our") collects, uses, and shares information about you when you visit this website.

Information we collect
- Usage data: pages viewed, device type, approximate location (city level), referring sites. This is collected via cookies and analytics services.
- Information you submit: email address and phone number when you subscribe to newsletters or breaking alerts; contact-form fields when you reach out.

How we use information
- To deliver our newsletters and alerts that you specifically opt in to.
- To respond to your inquiries.
- To measure and improve our site, and serve relevant advertising (including via third-party ad networks).

Sharing
We do not sell personal information. We share limited data with service providers (analytics, advertising, email/SMS infrastructure) bound by data-processing agreements.

Your rights
You can unsubscribe at any time via the link in every email/SMS, or via our Unsubscribe page. California residents: see the CCPA Notice.

Contact
For privacy questions, use our Contact page.
`,
  },
  terms: {
    title: "Terms of Use",
    body: (site) => `
Last updated: ${new Date().toLocaleDateString()}.

By accessing ${site.name}, you agree to the following Terms.

Content
Articles on this site may include summaries, analysis, and commentary drawn from third-party reporting linked within each article. All trademarks belong to their respective owners. We make commercially reasonable efforts to ensure accuracy but do not warrant completeness or timeliness.

No investment advice
Nothing on this site constitutes investment, legal, or tax advice. Do your own research and consult a licensed professional before making financial decisions.

Acceptable use
You agree not to misuse the site, attempt unauthorized access, scrape at scale, or interfere with normal operation.

Liability
To the maximum extent permitted by law, ${site.name} disclaims all warranties and shall not be liable for indirect or consequential damages arising from your use of the site.

Governing law
These Terms are governed by the laws of the State of Delaware, United States.
`,
  },
  ccpa: {
    title: "CCPA / CPRA Notice (California Residents)",
    body: (site) => `
Last updated: ${new Date().toLocaleDateString()}.

If you are a California resident, the California Consumer Privacy Act ("CCPA") as amended by the California Privacy Rights Act ("CPRA") grants you the following rights with respect to your personal information held by ${site.name}:

- Right to know what categories of personal information we collect, the sources, purposes, and any third parties with whom we share it.
- Right to delete personal information we have collected, subject to limited exceptions.
- Right to correct inaccurate personal information.
- Right to opt out of the "sale" or "sharing" of personal information for cross-context behavioral advertising.
- Right to limit use of sensitive personal information.
- Right not to be discriminated against for exercising any of these rights.

To exercise these rights, contact us via our Contact page or our Unsubscribe page. We will verify your request and respond within 45 days.

Do Not Sell or Share My Personal Information
We honor the Global Privacy Control (GPC) signal. You can also email us to make this request explicitly.
`,
  },
  about: {
    title: "About",
    body: (site) => `
${site.name}${site.tagline ? " — " + site.tagline : ""}.

${site.description || `${site.name} is an independent publication covering ${site.niche || "US business and finance"}. We summarize, analyze, and link to the most consequential stories of the day, presented in a focused, fast-reading format.`}

Editorial standards
- Every story attributes its primary source.
- We add original analysis to the reporting we surface — we do not republish.
- Corrections are appended publicly; please reach out via our Contact page if you spot an error.

Independence
${site.name} is operated by an independent publisher and accepts advertising. Editorial decisions are made without regard to advertiser interests.
`,
  },
};

export default function PublicLegal({ pageKey }) {
  const { slug } = useParams();
  const [site, setSite] = useState(null);
  useEffect(() => { publicApi.get(`/public/sites/${slug}/meta`).then(r => setSite(r.data)); }, [slug]);
  useSiteTheme(site);

  if (!site) return <div className="public-site min-h-screen flex items-center justify-center"><div className="opacity-60 mono">Loading…</div></div>;
  const page = PAGES[pageKey] || PAGES.privacy;

  return (
    <div className="public-site">
      <SeoHead site={site} title={page.title} description={`${page.title} for ${site.name}`} />
      <nav className="border-b" style={{ borderColor: "var(--site-rule)" }}>
        <div className="max-w-6xl mx-auto px-4 flex items-center gap-6 py-3 text-sm">
          <Link to={`/site/${site.slug}`} className="display text-lg font-bold">{site.name}</Link>
        </div>
      </nav>
      <article className="max-w-3xl mx-auto px-4 py-12">
        <h1 className="display text-4xl md:text-5xl font-black tracking-tight">{page.title}</h1>
        <div className="mt-6 space-y-4 text-base leading-[1.75] whitespace-pre-wrap opacity-90">
          {page.body(site)}
        </div>
      </article>
    </div>
  );
}
